import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiSettings, FiAlertTriangle, FiSmile, FiFileText, FiCode, FiUserPlus, FiServer, FiHardDrive } from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';
import { useApp } from '../context/AppContext';
import EmojiSettingsModal from './Modals/EmojiSettingsModal';
import TemplatesManagementModal from './Modals/TemplatesManagementModal';
import AppTitleSettingsModal from './Modals/AppTitleSettingsModal';
import FrontendSettingsModal from './Modals/FrontendSettingsModal';
import PendingRegistrationsModal from './Modals/PendingRegistrationsModal';
import ServerOneCMonitorModal from './Modals/ServerOneCMonitorModal';
import BackupControlModal from './Modals/BackupControlModal';
import OnecMassShutdownScheduleModal from './Modals/OnecMassShutdownScheduleModal';
import TelegramBotsModal from './Modals/TelegramBotsModal';
import {
  isAdminRole,
  canSeeManagementRegistrationAndAppTitle,
  canMassShutdown1cRole,
} from '../utils/adminRoles';
import { canAccessTelegramBotSettings } from '../utils/telegramBotAccess';
import GodModeControl from './GodModeControl';

export default function AdminDesktop({
  open,
  onClose,
}) {
  const { state } = useApp();
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Admin states
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminHost, setAdminHost] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [adminEnabledFilter, setAdminEnabledFilter] = useState('all');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newNoExpire, setNewNoExpire] = useState(true);
  const [editRow, setEditRow] = useState(null);
  const [showEmojiSettings, setShowEmojiSettings] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAppTitleSettings, setShowAppTitleSettings] = useState(false);
  const [showPendingRegistrations, setShowPendingRegistrations] = useState(false);
  const [showFrontendSettings, setShowFrontendSettings] = useState(false);
  const [showServerOneCMonitor, setShowServerOneCMonitor] = useState(false);
  const [showBackupControl, setShowBackupControl] = useState(false);
  const [showOnecMassShutdown, setShowOnecMassShutdown] = useState(false);
  const [showTelegramBots, setShowTelegramBots] = useState(false);

  // Load users when modal opens
  useEffect(() => {
    if (!open) return;
    async function loadUsers() {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Проверяем, не является ли сервер Linux (кэшируем результат)
      const isLinuxServer = sessionStorage.getItem('admin_is_linux_server') === 'true';
      if (isLinuxServer) {
        setAdminError('Управление локальными пользователями доступно только на Windows-серверах');
        setAdminUsers([]);
        setAdminLoading(false);
        return;
      }
      
      setAdminLoading(true);
      setAdminError('');
      try {
        try {
          const rh = await fetch('/api/admin/host', { headers: { Authorization: `Bearer ${token}` } });
          if (rh.ok) {
            const hd = await rh.json();
            if (hd && hd.success && hd.host) setAdminHost(hd.host);
          }
        } catch {}
        try {
          const r = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) {
            try {
              const data = await r.json();
              if (Array.isArray(data)) setAdminUsers(data);
              else setAdminUsers([]);
            } catch (jsonError) {
              console.error('Ошибка парсинга JSON:', jsonError);
              setAdminError('Не удалось обработать ответ сервера');
              setAdminUsers([]);
            }
          } else {
            // Пытаемся прочитать сообщение об ошибке
            let errorMessage = `Сервер вернул ошибку ${r.status}`;
            let isWindowsError = false;
            try {
              const errorText = await r.text();
              try {
                const errorJson = JSON.parse(errorText);
                if (errorJson.error) {
                  errorMessage = errorJson.error;
                  // Понятные сообщения для известных ошибок
                  if (errorMessage.includes('powershell.exe') || errorMessage.includes('Windows systems') || errorMessage.includes('only available on Windows')) {
                    errorMessage = 'Управление локальными пользователями доступно только на Windows-серверах';
                    isWindowsError = true;
                    // Кэшируем, что сервер на Linux, чтобы не делать повторные запросы
                    sessionStorage.setItem('admin_is_linux_server', 'true');
                  } else if (errorMessage.includes('ENOENT')) {
                    errorMessage = 'Ошибка: системная команда недоступна на данном сервере';
                  }
                } else if (errorJson.message) {
                  errorMessage = errorJson.message;
                  if (errorMessage.includes('Windows systems') || errorMessage.includes('only available on Windows')) {
                    errorMessage = 'Управление локальными пользователями доступно только на Windows-серверах';
                    isWindowsError = true;
                    // Кэшируем, что сервер на Linux, чтобы не делать повторные запросы
                    sessionStorage.setItem('admin_is_linux_server', 'true');
                  }
                }
              } catch {
                // Если не JSON, оставляем текст как есть
                if (errorText && errorText.length < 200) {
                  errorMessage = errorText;
                  if (errorMessage.includes('Windows systems') || errorMessage.includes('only available on Windows')) {
                    errorMessage = 'Управление локальными пользователями доступно только на Windows-серверах';
                    isWindowsError = true;
                    // Кэшируем, что сервер на Linux, чтобы не делать повторные запросы
                    sessionStorage.setItem('admin_is_linux_server', 'true');
                  }
                }
              }
            } catch {}
            
            // Для ошибок Windows не логируем в консоль, так как это ожидаемое поведение
            if (!isWindowsError) {
              console.error('Ошибка загрузки пользователей:', r.status, r.statusText);
            }
            
            setAdminError(isWindowsError ? errorMessage : `Не удалось получить пользователей: ${errorMessage}`);
            setAdminUsers([]);
          }
        } catch (e) {
          console.error('Ошибка запроса пользователей:', e);
          setAdminError('Не удалось получить пользователей: ' + (e.message || 'неизвестная ошибка'));
          setAdminUsers([]);
        }
      } catch (e) {
        console.error('Общая ошибка загрузки:', e);
        setAdminError('Не удалось получить пользователей: ' + (e.message || 'неизвестная ошибка'));
        setAdminUsers([]);
      } finally {
        setAdminLoading(false);
      }
    }
    if (open) loadUsers();
  }, [open]);

  const adminCreateUser = async (addRdpParam) => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch('/api/admin/local-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newName, password: newPassword, description: newDescription, noExpire: newNoExpire, addToRdp: addRdpParam })
      });
      const data = await r.json();
      if (!r.ok || data?.success === false) throw new Error(data?.error || 'Ошибка создания');
      const createdName = newName;
      setNewName(''); setNewPassword(''); setNewDescription(''); setNewNoExpire(true);
      const rr = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
      const users = await rr.json();
      setAdminUsers(Array.isArray(users) ? users : []);
      if (addRdpParam && createdName) {
        try {
          await fetch(`/api/admin/local-users/${encodeURIComponent(createdName)}/rdp`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ add: true }) });
        } catch {}
      }
      alert('Пользователь создан');
    } catch (e) {
      alert('Ошибка: ' + e.message);
    }
  };

  const adminRefreshUsers = async () => {
    const token = localStorage.getItem('token');
    try {
      setAdminLoading(true);
      const r = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      alert('Ошибка обновления списка: ' + e.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');
    try {
      let currentUserName = editRow.name;
      
      if (editRow.name && editRow.name !== (editRow.originalName || '')) {
        const renameRes = await fetch(`/api/admin/local-users/${encodeURIComponent(editRow.originalName)}/rename`, { 
          method:'POST', 
          headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, 
          body: JSON.stringify({ newName: editRow.name }) 
        });
        if (!renameRes.ok) {
          const errorData = await renameRes.json().catch(() => ({ message: 'Ошибка переименования' }));
          throw new Error(errorData.message || `Ошибка переименования: ${renameRes.status}`);
        }
        currentUserName = editRow.name;
      }
      
      const descRes = await fetch(`/api/admin/local-users/${encodeURIComponent(currentUserName)}/description`, { 
        method:'POST', 
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, 
        body: JSON.stringify({ description: editRow.description || '' }) 
      });
      if (!descRes.ok) {
        const errorData = await descRes.json().catch(() => ({ message: 'Ошибка обновления описания' }));
        throw new Error(errorData.message || `Ошибка обновления описания: ${descRes.status}`);
      }
      
      const noExpireRes = await fetch(`/api/admin/local-users/${encodeURIComponent(currentUserName)}/no-expire`, { 
        method:'POST', 
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, 
        body: JSON.stringify({ noExpire: !!editRow.noExpire }) 
      });
      if (!noExpireRes.ok) {
        const errorData = await descRes.json().catch(() => ({ message: 'Ошибка обновления флага no-expire' }));
        throw new Error(errorData.message || `Ошибка обновления флага no-expire: ${noExpireRes.status}`);
      }
      
      if ((editRow.password || '').trim().length > 0) {
        const passwordRes = await fetch(`/api/admin/local-users/${encodeURIComponent(currentUserName)}/password`, { 
          method:'POST', 
          headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, 
          body: JSON.stringify({ password: editRow.password.trim() }) 
        });
        if (!passwordRes.ok) {
          const errorData = await passwordRes.json().catch(() => ({ error: 'Ошибка изменения пароля' }));
          throw new Error(errorData.error || errorData.message || `Ошибка изменения пароля: ${passwordRes.status}`);
        }
      }
      
      const r = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        throw new Error(`Ошибка загрузки списка пользователей: ${r.status}`);
      }
      const users = await r.json();
      setAdminUsers(Array.isArray(users) ? users : []);
      setEditRow(null);
      alert('Изменения сохранены успешно');
    } catch (e) {
      alert('Ошибка сохранения: ' + e.message);
      console.error('Ошибка сохранения пользователя:', e);
    }
  };

  const adminRdp = async (name, add) => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`/api/admin/local-users/${encodeURIComponent(name)}/rdp`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ add }) });
      const data = await r.json();
      if (!r.ok || data?.success === false) throw new Error(data?.error || 'Ошибка');
      alert(add ? 'Добавлен в группу RDP' : 'Удален из группы RDP');
    } catch (e) {
      alert('Ошибка: ' + e.message);
    }
  };

  const adminEnable = async (name, enable) => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`/api/admin/local-users/${encodeURIComponent(name)}/${enable ? 'enable' : 'disable'}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok || data?.success === false) throw new Error(data?.error || 'Ошибка');
      const rr = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
      const users = await rr.json();
      setAdminUsers(Array.isArray(users) ? users : []);
      if (editRow && editRow.name === name) {
        setEditRow({...editRow, enabled: enable});
      }
    } catch (e) {
      alert('Ошибка: ' + e.message);
    }
  };

  const adminDelete = async (name) => {
    if (!window.confirm('Удалить пользователя? Это действие нельзя отменить.')) return;
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`/api/admin/local-users/${encodeURIComponent(name)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok || data?.success === false) throw new Error(data?.error || 'Ошибка');
      const rr = await fetch('/api/admin/local-users', { headers: { Authorization: `Bearer ${token}` } });
      const users = await rr.json();
      setAdminUsers(Array.isArray(users) ? users : []);
      if (editRow && editRow.name === name) {
        setEditRow(null);
      }
      alert('Пользователь удален');
    } catch (e) {
      alert('Ошибка: ' + e.message);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!open) return null;

  const filteredUsers = adminUsers
    .filter(u => !adminFilter || (u?.Name || '').toLowerCase().includes(adminFilter.toLowerCase()))
    .filter(u => {
      if (adminEnabledFilter === 'all') return true;
      if (adminEnabledFilter === 'enabled') return !!u?.Enabled;
      return !u?.Enabled;
    });

  return (
    <>
    <div
      ref={overlayRef}
      id="admin-desktop-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.45)',
        zIndex: 500000,
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        margin: 0,
        padding: 0
      }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: 0,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          color: '#fff',
          overflow: 'hidden',
          borderRadius: 0,
          boxShadow: 'none',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box'
        }}
      >
        {/* Header — в потоке flex-колонки; fixed+left:0 ломал раскладку и давал «одну полоску» */}
        <div
          data-admin-desktop-header
          style={{
            flexShrink: 0,
            width: '100%',
            maxWidth: '100%',
            height: '64px',
            backgroundColor: 'rgba(35, 41, 49, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 32px',
            zIndex: 4,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pointerEvents: 'auto',
            boxSizing: 'border-box'
          }}
        >
          <h2
            style={{
              color: '#a3e635',
              fontSize: '24px',
              fontWeight: 900,
              margin: 0,
              flex: 1,
              textAlign: 'center',
            }}
          >
            Управление
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GodModeControl compact />
            <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            title="Закрыть"
          >
            <FiX />
          </button>
          </div>
        </div>

        {/* Content — minHeight:0 обязателен для flex:1 + overflow внутри колонки */}
        <div
          data-admin-desktop-body
          style={{
            padding: '32px 48px',
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'auto',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          {/* Информация о хосте */}
          <div style={{
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#93c5fd',
            fontSize: '15px'
          }}>
            <strong>Хост:</strong> <span style={{color:'#b2ffb2', fontWeight:700}}>{adminHost || '...'}</span>
          </div>

          {/* Кнопки управления */}
          <div style={{ display:'flex', flexWrap: 'wrap', gap:12, marginBottom:24 }}>
            {isAdminRole(state.user?.role, state.user) && (
              <button
                onClick={() => {
                  setShowEmojiSettings(true);
                }}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(255,193,7,0.45)',
                  background:'rgba(255,193,7,0.18)',
                  color:'#ffd43b',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiSmile size={20} /> Управление эмодзи
              </button>
            )}
            <button
              onClick={() => {
                setShowTemplates(true);
              }}
              style={{
                padding:'14px 20px',
                borderRadius:12,
                border:'1px solid rgba(59,130,246,0.45)',
                background:'rgba(59,130,246,0.18)',
                color:'#93c5fd',
                cursor:'pointer',
                fontWeight:700,
                display:'flex',
                alignItems:'center',
                gap:10,
                fontSize: '15px',
                minWidth: '200px',
                justifyContent: 'center'
              }}
            >
              <FiFileText size={20} /> Шаблоны
            </button>
            {canAccessTelegramBotSettings(state.user) && (
              <button
                type="button"
                onClick={() => setShowTelegramBots(true)}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(14,165,233,0.5)',
                  background:'rgba(14,165,233,0.15)',
                  color:'#7dd3fc',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FaTelegramPlane size={20} /> Бот
              </button>
            )}
            {canSeeManagementRegistrationAndAppTitle(state.user?.role, state.user) && (
              <button
                onClick={() => setShowPendingRegistrations(true)}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(56,189,248,0.4)',
                  background:'rgba(56,189,248,0.12)',
                  color:'#7dd3fc',
                  cursor:'pointer',
                  fontWeight:600,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiUserPlus size={20} /> Заявки на регистрацию
              </button>
            )}
            {canSeeManagementRegistrationAndAppTitle(state.user?.role, state.user) && (
              <button
                onClick={() => {
                  setShowAppTitleSettings(true);
                }}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(163,230,53,0.45)',
                  background:'rgba(163,230,53,0.18)',
                  color:'#a3e635',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiSettings size={20} /> Управление названием приложения
              </button>
            )}
            {isAdminRole(state.user?.role, state.user) && (
              <button
                onClick={() => {
                  setShowFrontendSettings(true);
                }}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(67,233,123,0.45)',
                  background:'rgba(67,233,123,0.18)',
                  color:'#43e97b',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiCode size={20} /> Управление фронтендом
              </button>
            )}
            {isAdminRole(state.user?.role, state.user) && (
              <button
                type="button"
                onClick={() => setShowServerOneCMonitor(true)}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(56,189,248,0.45)',
                  background:'rgba(56,189,248,0.14)',
                  color:'#7dd3fc',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiServer size={20} /> Мониторинг сервера 1С
              </button>
            )}
            {isAdminRole(state.user?.role, state.user) && (
              <button
                type="button"
                onClick={() => setShowBackupControl(true)}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(167,139,250,0.45)',
                  background:'rgba(167,139,250,0.14)',
                  color:'#ddd6fe',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiHardDrive size={20} /> Бэкап сервера и учёток
              </button>
            )}
            {canMassShutdown1cRole(state.user?.role, state.user) && (
              <button
                type="button"
                onClick={() => setShowOnecMassShutdown(true)}
                style={{
                  padding:'14px 20px',
                  borderRadius:12,
                  border:'1px solid rgba(239,68,68,0.45)',
                  background:'rgba(239,68,68,0.18)',
                  color:'#fecaca',
                  cursor:'pointer',
                  fontWeight:700,
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  fontSize: '15px',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <FiAlertTriangle size={20} /> Выбросить всех с 1С
              </button>
            )}
          </div>

          {/* Админские функции */}
          {isAdminRole(state.user?.role, state.user) && (
            <>
              {/* Фильтры и обновление */}
              <div style={{ display:'flex', alignItems: 'center', gap:12, marginBottom:24, flexWrap: 'wrap' }}>
                <button
                  onClick={adminRefreshUsers}
                  style={{
                    padding:'14px 20px',
                    borderRadius:12,
                    border:'1px solid rgba(67,233,123,0.35)',
                    background:'rgba(67,233,123,0.15)',
                    color:'#b2ffb2',
                    cursor:'pointer',
                    fontWeight:700,
                    fontSize: '15px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {adminLoading ? 'Загрузка...' : 'Обновить список пользователей'}
                </button>
                <input
                  value={adminFilter}
                  onChange={e=>setAdminFilter(e.target.value)}
                  placeholder="Поиск пользователя..."
                  style={{
                    padding:'12px 16px',
                    borderRadius:10,
                    border:'1px solid #2f3440',
                    background:'#0e1420',
                    color:'#fff',
                    fontSize: '16px',
                    minWidth: 240,
                    flex: 1,
                    maxWidth: '400px',
                    boxSizing: 'border-box'
                  }}
                />
                <select
                  value={adminEnabledFilter}
                  onChange={e=>setAdminEnabledFilter(e.target.value)}
                  style={{
                    padding:'12px 16px',
                    borderRadius:10,
                    border:'1px solid #2f3440',
                    background:'#0e1420',
                    color:'#fff',
                    fontSize: '16px',
                    minWidth: 150,
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">Все</option>
                  <option value="enabled">Включен</option>
                  <option value="disabled">Отключен</option>
                </select>
              </div>

              {/* Две колонки: создание и список */}
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                {/* Левая колонка: Создание пользователя */}
                <div style={{
                  background:'rgba(255,255,255,0.04)',
                  borderRadius:16,
                  padding:24,
                  minWidth: '400px',
                  flex: '0 0 400px'
                }}>
                  <h3 style={{marginTop:0, color:'#fff', fontSize: '18px', marginBottom: 16}}>Создать локального пользователя</h3>
                  <div style={{display:'flex', flexDirection:'column', gap:12}}>
                    <input 
                      value={newName} 
                      onChange={e=>setNewName(e.target.value)} 
                      placeholder="Имя пользователя" 
                      style={{
                        padding:12, 
                        fontSize:16, 
                        borderRadius:10, 
                        border:'1px solid #333', 
                        background:'#111', 
                        color:'#fff',
                        boxSizing: 'border-box',
                        width: '100%'
                      }} 
                    />
                    <input 
                      type="text" 
                      value={newPassword} 
                      onChange={e=>setNewPassword(e.target.value)} 
                      placeholder="Пароль" 
                      style={{
                        padding:12, 
                        fontSize:16, 
                        borderRadius:10, 
                        border:'1px solid #333', 
                        background:'#111', 
                        color:'#fff',
                        boxSizing: 'border-box',
                        width: '100%'
                      }} 
                    />
                    <input 
                      value={newDescription} 
                      onChange={e=>setNewDescription(e.target.value)} 
                      placeholder="Описание" 
                      style={{
                        padding:12, 
                        fontSize:16, 
                        borderRadius:10, 
                        border:'1px solid #333', 
                        background:'#111', 
                        color:'#fff',
                        boxSizing: 'border-box',
                        width: '100%'
                      }} 
                    />
                    <label style={{display:'flex', alignItems:'center', gap:8, color:'#ddd', fontSize: '15px'}}>
                      <input 
                        type="checkbox" 
                        checked={newNoExpire} 
                        onChange={e=>setNewNoExpire(e.target.checked)} 
                        style={{ width: '20px', height: '20px' }}
                      />
                      Пароль не истекает
                    </label>
                    <div style={{display:'flex', flexDirection: 'column', gap:8}}>
                      <button 
                        onClick={()=>adminCreateUser(false)} 
                        style={{
                          padding:'14px 16px', 
                          fontSize:16, 
                          borderRadius:10, 
                          border:'1px solid #334155', 
                          background:'#0f172a', 
                          color:'#e2e8f0', 
                          cursor:'pointer',
                          width: '100%',
                          fontWeight: 600
                        }}
                      >
                        Создать
                      </button>
                      <button 
                        onClick={()=>adminCreateUser(true)} 
                        style={{
                          padding:'14px 16px', 
                          fontSize:16, 
                          borderRadius:10, 
                          border:'1px solid rgba(67,233,123,0.35)', 
                          background:'rgba(67,233,123,0.15)', 
                          color:'#b2ffb2', 
                          cursor:'pointer', 
                          fontWeight:700,
                          width: '100%'
                        }}
                      >
                        Создать и добавить в RDP
                      </button>
                    </div>
                  </div>
                </div>

                {/* Правая колонка: Список пользователей */}
                <div style={{
                  background:'rgba(255,255,255,0.04)',
                  borderRadius:16,
                  padding:24,
                  flex: 1,
                  minWidth: 0
                }}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 16}}>
                    <h3 style={{marginTop:0, color:'#fff', fontSize: '18px'}}>Локальные пользователи</h3>
                    {adminLoading && <div style={{color:'#aaa', fontSize: '14px'}}>Загрузка...</div>}
                  </div>
                  {adminError && <div style={{color:'#fca5a5', marginBottom: 16, fontSize: '14px'}}>{adminError}</div>}
                  <div style={{
                    overflowY:'auto',
                    overflowX:'auto',
                    maxHeight:'calc(100vh - 400px)',
                    WebkitOverflowScrolling:'touch',
                    borderRadius:12,
                    border:'1px solid #2a2f37'
                  }}>
                    <div style={{ minWidth: '100%', display: 'table' }}>
                      <div style={{ display: 'table-header-group', background: '#111827', position: 'sticky', top: 0, zIndex: 1 }}>
                        <div style={{ display: 'table-row' }}>
                          <div style={{ display: 'table-cell', padding:'12px 16px', fontWeight:700, color:'#b2bec3', fontSize: '14px', borderBottom: '1px solid #2a2f37', width: '30%' }}>Имя</div>
                          <div style={{ display: 'table-cell', padding:'12px 16px', fontWeight:700, color:'#b2bec3', fontSize: '14px', borderBottom: '1px solid #2a2f37', width: '20%' }}>Статус</div>
                          <div style={{ display: 'table-cell', padding:'12px 16px', fontWeight:700, color:'#b2bec3', fontSize: '14px', borderBottom: '1px solid #2a2f37', width: '35%' }}>Описание</div>
                          <div style={{ display: 'table-cell', padding:'12px 16px', fontWeight:700, color:'#b2bec3', fontSize: '14px', borderBottom: '1px solid #2a2f37', width: '15%' }}>Действия</div>
                        </div>
                      </div>
                      <div style={{ display: 'table-row-group' }}>
                        {filteredUsers.map((u, idx) => (
                          <div
                            key={`${u.Name || 'user'}-${idx}`}
                            style={{
                              display: 'table-row',
                              borderTop:'1px solid #2a2f37',
                              borderBottom:'1px solid #2a2f37',
                              background: idx % 2 === 0 ? '#0f1424' : '#0b1120'
                            }}
                          >
                            <div style={{display: 'table-cell', padding:'12px 16px', color:'#fff', fontSize: '14px', borderLeft:'3px solid #1f2937'}} title={u.Name}>{u.Name}</div>
                            <div style={{display: 'table-cell', padding:'12px 16px', fontSize: '14px'}}>
                              <span style={{color: u.Enabled ? '#b2ffb2' : '#fca5a5'}}>{u.Enabled ? 'Включен' : 'Отключен'}</span>
                            </div>
                            <div style={{display: 'table-cell', padding:'12px 16px', color:'#ccc', fontSize: '14px'}} title={u.Description}>{u.Description || '-'}</div>
                            <div style={{display: 'table-cell', padding:'12px 16px'}}>
                              <button 
                                onClick={()=>setEditRow({ name: u.Name, originalName: u.Name, description: u.Description || '', password: '', noExpire: false, enabled: u.Enabled })} 
                                style={{
                                  padding:'10px 14px', 
                                  borderRadius:8, 
                                  border:'1px solid #334155', 
                                  background:'#0f172a', 
                                  color:'#93c5fd', 
                                  cursor:'pointer',
                                  fontSize: '14px',
                                  width: '100%',
                                  fontWeight: 600
                                }}
                              >
                                Изменить
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {filteredUsers.length === 0 && (
                      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', fontSize: '15px' }}>
                        {adminLoading ? 'Загрузка...' : 'Нет пользователей'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Модалка редактирования пользователя */}
        {editRow && ReactDOM.createPortal(
          <div 
            style={{
              position:'fixed', 
              inset:0, 
              zIndex:600000, 
              display:'flex', 
              alignItems:'center', 
              justifyContent:'center',
              background: 'rgba(0, 0, 0, 0.9)',
              padding: '16px'
            }}
            onClick={()=>setEditRow(null)}
          >
            <div 
              style={{
                position:'relative', 
                width:'100%',
                maxWidth: '600px',
                background:'linear-gradient(135deg, #232931 0%, #181c22 100%)', 
                borderRadius:20, 
                padding:24, 
                boxShadow:'0 10px 40px rgba(0,0,0,0.4)', 
                color:'#fff',
                maxHeight: '90vh',
                overflowY: 'auto'
              }} 
              onClick={e=>e.stopPropagation()}
            >
              <button 
                onClick={()=>setEditRow(null)} 
                style={{
                  position:'absolute', 
                  top:10, 
                  right:10, 
                  background:'transparent', 
                  border:'none', 
                  color:'#fff', 
                  fontSize:24, 
                  cursor:'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <h3 style={{marginTop:0, marginBottom:16, fontWeight:900, fontSize: '20px'}}>Изменить пользователя</h3>
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                <div>
                  <label style={{display:'block', fontSize:12, color:'#9ca3af', marginBottom:6}}>Новое имя</label>
                  <input 
                    value={editRow.name} 
                    onChange={e=>setEditRow({...editRow, name:e.target.value})} 
                    style={{
                      width:'100%', 
                      padding:12, 
                      borderRadius:10, 
                      border:'1px solid #2f3440', 
                      background:'#0e1420', 
                      color:'#fff',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }} 
                  />
                </div>
                <div>
                  <label style={{display:'block', fontSize:12, color:'#9ca3af', marginBottom:6}}>Описание</label>
                  <input 
                    value={editRow.description} 
                    onChange={e=>setEditRow({...editRow, description:e.target.value})} 
                    style={{
                      width:'100%', 
                      padding:12, 
                      borderRadius:10, 
                      border:'1px solid #2f3440', 
                      background:'#0e1420', 
                      color:'#fff',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }} 
                  />
                </div>
                <div>
                  <label style={{display:'block', fontSize:12, color:'#9ca3af', marginBottom:6}}>Новый пароль (по желанию)</label>
                  <input 
                    type="text" 
                    value={editRow.password || ''} 
                    onChange={e=>setEditRow({...editRow, password:e.target.value})} 
                    style={{
                      width:'100%', 
                      padding:12, 
                      borderRadius:10, 
                      border:'1px solid #2f3440', 
                      background:'#0e1420', 
                      color:'#fff',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }} 
                  />
                </div>
                <div>
                  <label style={{display:'block', fontSize:12, color:'#9ca3af', marginBottom:6}}>Членство в RDP</label>
                  <div style={{display:'flex', gap:8, flexDirection: 'column'}}>
                    <button 
                      onClick={async()=>{ 
                        try { 
                          const t = localStorage.getItem('token'); 
                          await fetch(`/api/admin/local-users/${encodeURIComponent(editRow.name)}/rdp`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${t}` }, body: JSON.stringify({ add: true }) }); 
                          alert('Добавлен в RDP'); 
                        } catch(e){ alert('Ошибка: '+e.message); } 
                      }} 
                      style={{
                        padding:'12px 16px', 
                        borderRadius:8, 
                        border:'1px solid #334155', 
                        background:'#0f172a', 
                        color:'#b2ffb2', 
                        cursor:'pointer',
                        fontSize: '15px',
                        width: '100%',
                        fontWeight: 600
                      }}
                    >
                      Добавить в RDP
                    </button>
                    <button 
                      onClick={async()=>{ 
                        try { 
                          const t = localStorage.getItem('token'); 
                          await fetch(`/api/admin/local-users/${encodeURIComponent(editRow.name)}/rdp`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${t}` }, body: JSON.stringify({ add: false }) }); 
                          alert('Убран из RDP'); 
                        } catch(e){ alert('Ошибка: '+e.message); } 
                      }} 
                      style={{
                        padding:'12px 16px', 
                        borderRadius:8, 
                        border:'1px solid #334155', 
                        background:'#0f172a', 
                        color:'#fecaca', 
                        cursor:'pointer',
                        fontSize: '15px',
                        width: '100%',
                        fontWeight: 600
                      }}
                    >
                      Убрать из RDP
                    </button>
                  </div>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:10, color:'#ddd', marginTop:10}}>
                  <label style={{display:'flex', alignItems:'center', gap:8, fontSize: '15px'}}>
                    <input 
                      type="checkbox" 
                      checked={!!editRow.noExpire} 
                      onChange={e=>setEditRow({...editRow, noExpire:e.target.checked})} 
                      style={{ width: '20px', height: '20px' }}
                    /> 
                    Пароль не истекает
                  </label>
                  <button
                    onClick={async ()=>{
                      try {
                        const token = localStorage.getItem('token');
                        await fetch(`/api/admin/local-users/${encodeURIComponent(editRow.name)}/no-expire`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ noExpire: true }) });
                        alert('Флаг «пароль не истекает» установлен');
                      } catch(e){ alert('Ошибка: '+e.message); }
                    }}
                    style={{
                      padding:'10px 14px', 
                      borderRadius:8, 
                      border:'1px solid rgba(67,233,123,0.35)', 
                      background:'rgba(67,233,123,0.12)', 
                      color:'#b2ffb2', 
                      cursor:'pointer',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    Применить сейчас
                  </button>
                </div>
                <div style={{display:'flex', flexDirection: 'column', gap:10, marginTop:16}}>
                  <div style={{display:'flex', gap:8, flexDirection: 'column'}}>
                    {editRow.enabled ? (
                      <button 
                        onClick={async ()=>{ 
                          try { 
                            await adminEnable(editRow.name, false);
                            alert('Пользователь отключен'); 
                          } catch(e){ alert('Ошибка: '+e.message); } 
                        }} 
                        style={{
                          padding:'12px 16px', 
                          borderRadius:10, 
                          border:'1px solid rgba(239,68,68,0.35)', 
                          background:'rgba(239,68,68,0.15)', 
                          color:'#fecaca', 
                          cursor:'pointer',
                          fontSize: '15px',
                          width: '100%',
                          fontWeight: 600
                        }}
                      >
                        Отключить
                      </button>
                    ) : (
                      <button 
                        onClick={async ()=>{ 
                          try { 
                            await adminEnable(editRow.name, true);
                            alert('Пользователь включен'); 
                          } catch(e){ alert('Ошибка: '+e.message); } 
                        }} 
                        style={{
                          padding:'12px 16px', 
                          borderRadius:10, 
                          border:'1px solid rgba(67,233,123,0.35)', 
                          background:'rgba(67,233,123,0.15)', 
                          color:'#b2ffb2', 
                          cursor:'pointer',
                          fontSize: '15px',
                          width: '100%',
                          fontWeight: 600
                        }}
                      >
                        Включить
                      </button>
                    )}
                    <button 
                      onClick={async ()=>{ 
                        if(window.confirm('Удалить пользователя? Это действие нельзя отменить.')){ 
                          try { 
                            await adminDelete(editRow.name);
                          } catch(e){ alert('Ошибка: '+e.message); } 
                        } 
                      }} 
                      style={{
                        padding:'12px 16px', 
                        borderRadius:10, 
                        border:'1px solid rgba(239,68,68,0.45)', 
                        background:'rgba(239,68,68,0.18)', 
                        color:'#fecaca', 
                        cursor:'pointer',
                        fontSize: '15px',
                        width: '100%',
                        fontWeight: 600
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                  <div style={{display:'flex', gap:10, flexDirection: 'column'}}>
                    <button 
                      onClick={()=>setEditRow(null)} 
                      style={{
                        padding:'12px 16px', 
                        borderRadius:10, 
                        border:'1px solid #374151', 
                        background:'#111827', 
                        color:'#e5e7eb', 
                        cursor:'pointer',
                        fontSize: '15px',
                        width: '100%',
                        fontWeight: 600
                      }}
                    >
                      Отмена
                    </button>
                    <button 
                      onClick={handleSaveEdit} 
                      style={{
                        padding:'12px 16px', 
                        borderRadius:10, 
                        border:'1px solid rgba(67,233,123,0.35)', 
                        background:'rgba(67,233,123,0.15)', 
                        color:'#b2ffb2', 
                        cursor:'pointer', 
                        fontWeight:700,
                        fontSize: '15px',
                        width: '100%'
                      }}
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
      
      {/* Emoji Settings Modal - десктопная версия */}
      {showEmojiSettings && ReactDOM.createPortal(
        <EmojiSettingsModal
          open={showEmojiSettings}
          onClose={() => setShowEmojiSettings(false)}
        />,
        document.body
      )}
      
      {/* Templates Management Modal - десктопная версия */}
      {showTemplates && ReactDOM.createPortal(
        <TemplatesManagementModal
          open={showTemplates}
          onClose={() => setShowTemplates(false)}
        />,
        document.body
      )}
      
      {/* App Title Settings Modal - десктопная версия */}
      {showAppTitleSettings && ReactDOM.createPortal(
        <AppTitleSettingsModal
          open={showAppTitleSettings}
          onClose={() => setShowAppTitleSettings(false)}
        />,
        document.body
      )}
      
      {/* Frontend Settings Modal - десктопная версия */}
      {showFrontendSettings && ReactDOM.createPortal(
        <FrontendSettingsModal
          open={showFrontendSettings}
          onClose={() => setShowFrontendSettings(false)}
          isMobile={false}
        />,
        document.body
      )}

      {showPendingRegistrations &&
        ReactDOM.createPortal(
          <PendingRegistrationsModal
            open={showPendingRegistrations}
            onClose={() => setShowPendingRegistrations(false)}
            isMobile={false}
            sidebarOffset={0}
          />,
          document.body
        )}

      {showServerOneCMonitor &&
        ReactDOM.createPortal(
          <ServerOneCMonitorModal
            open={showServerOneCMonitor}
            onClose={() => setShowServerOneCMonitor(false)}
            isMobile={false}
            fullViewport
          />,
          document.body
        )}

      {showBackupControl &&
        ReactDOM.createPortal(
          <BackupControlModal
            open={showBackupControl}
            onClose={() => setShowBackupControl(false)}
            isMobile={false}
            fullViewport
          />,
          document.body
        )}

      {showTelegramBots &&
        ReactDOM.createPortal(
          <TelegramBotsModal
            open={showTelegramBots}
            onClose={() => setShowTelegramBots(false)}
            isMobile={false}
            sidebarOffset={320}
          />,
          document.body
        )}

      {showOnecMassShutdown &&
        ReactDOM.createPortal(
          <OnecMassShutdownScheduleModal
            open={showOnecMassShutdown}
            onClose={() => setShowOnecMassShutdown(false)}
            isMobile={false}
            sidebarOffset={320}
          />,
          document.body
        )}

    </>
  );
}

